<!-- eslint-disable vue/multi-word-component-names -->
<!-- eslint-disable-next-line vue/valid-template-root -->
<template></template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { PipeDisplayMode, usePipeIfAvailable } from '@render/composables/usePipe'
import { useComponentProcessors } from '@render/composables/useComponentProcessors'
import { useEditorCommands } from '@render/composables/useEditorCommands'
// Type-only import — erased at compile time, so the editor-only definition
// never enters a built-pipe bundle (see reference/06-component-metadata.md).
import type { TimerFields } from './definition'

defineOptions({ inheritAttrs: false })

const fields = defineModel<TimerFields>('fields', {
  required: true,
  default: (): TimerFields => ({}),
})

const emit = defineEmits<{
  (e: 'onTimerZero'): void
}>()

const pipe = usePipeIfAvailable()
const isEditor = computed(() => pipe?.currentMode.value === PipeDisplayMode.EDITOR)

const initialSeconds = computed(() => {
  const raw = Number(fields.value.durationSeconds)
  return Number.isFinite(raw) && raw >= 0 ? Math.floor(raw) : 0
})

// Wall-clock-based countdown: `startTimestamp` is the Date.now() of the
// current run (null while paused / stopped / reset); `durationAtStart` is
// the seconds value the current run was started with. `remaining` is
// recomputed from those on each tick rather than decremented, so inactive-
// tab setInterval throttling doesn't accumulate drift — a delayed tick
// reads the correct elapsed time in one go.
const startTimestamp = ref<number | null>(null)
const durationAtStart = ref<number>(0)
const remaining = ref<number>(0)

let intervalId: ReturnType<typeof setInterval> | null = null

function computeRemaining(): number {
  if (startTimestamp.value === null) return durationAtStart.value
  const elapsedSec = Math.floor((Date.now() - startTimestamp.value) / 1000)
  return Math.max(0, durationAtStart.value - elapsedSec)
}

function writeVariable(secs: number) {
  const key = fields.value.valueVariable?.key
  if (!key) return
  pipe?.setData(`var.${key}`, secs)
}

function tick() {
  const prev = remaining.value
  const next = computeRemaining()
  remaining.value = next
  writeVariable(next)

  if (prev > 0 && next === 0) {
    emit('onTimerZero')
    if (fields.value.loop) {
      // Restart the cycle from the configured initial duration. The override
      // (if any) only affects the cycle it was started with — loops always
      // return to the field-configured initial duration.
      durationAtStart.value = initialSeconds.value
      startTimestamp.value = Date.now()
      remaining.value = durationAtStart.value
      writeVariable(remaining.value)
    } else {
      stopInterval()
      startTimestamp.value = null
    }
  }
}

function startInterval() {
  if (intervalId !== null) return
  intervalId = setInterval(tick, 1000)
}

function stopInterval() {
  if (intervalId !== null) {
    clearInterval(intervalId)
    intervalId = null
  }
}

function startTimer(overrideSeconds?: unknown) {
  const override = Number(overrideSeconds)
  const baseDuration =
    Number.isFinite(override) && override >= 0
      ? Math.floor(override)
      : remaining.value > 0
        ? remaining.value
        : initialSeconds.value

  durationAtStart.value = baseDuration
  remaining.value = baseDuration
  writeVariable(baseDuration)

  if (baseDuration === 0) {
    // Nothing to count down; emit zero immediately so onTimerZero still fires
    // for consumers that expect an end signal on a zero-duration start.
    startTimestamp.value = null
    stopInterval()
    emit('onTimerZero')
    return
  }

  startTimestamp.value = Date.now()
  // Tick in any mode — authors preview the countdown from the editor via
  // the `previewStart` editor command, so the interval has to run even when
  // `isEditor` is true. Auto-start-on-mount still respects editor mode.
  startInterval()
}

function stopTimer() {
  // Capture currently-remaining time into durationAtStart so a subsequent
  // `start` with no override resumes from where the timer was paused.
  durationAtStart.value = remaining.value
  startTimestamp.value = null
  stopInterval()
}

function resetTimer() {
  stopInterval()
  startTimestamp.value = null
  durationAtStart.value = initialSeconds.value
  remaining.value = initialSeconds.value
  writeVariable(remaining.value)
}

useComponentProcessors({
  start: ({ config }) => startTimer(config?.override),
  stop: () => stopTimer(),
  reset: () => resetTimer(),
})

useEditorCommands({
  previewStart: () => startTimer(),
  previewReset: () => resetTimer(),
})

// Snappier recovery when the tab becomes visible again: the next interval
// firing may be up to 1s away, but we can recompute immediately on the
// `visible` transition so the displayed mm:ss catches up right away.
function onVisibilityChange() {
  if (typeof document === 'undefined') return
  if (document.visibilityState === 'visible' && startTimestamp.value !== null) {
    tick()
  }
}

onMounted(() => {
  // Seed the variable so interpolations show the initial value from the
  // first frame, rather than an empty cell until the first tick lands.
  remaining.value = initialSeconds.value
  durationAtStart.value = initialSeconds.value
  writeVariable(remaining.value)

  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisibilityChange)
  }

  if (!isEditor.value && fields.value.startOnLoad !== false) {
    startTimer()
  }
})

onBeforeUnmount(() => {
  stopInterval()
  if (typeof document !== 'undefined') {
    document.removeEventListener('visibilitychange', onVisibilityChange)
  }
})

// Editor→live transition: auto-start if configured to start on load and
// nothing's running yet. We deliberately do NOT stop the interval when
// entering editor mode — authors preview the countdown from the editor via
// the `previewStart` / `previewReset` editor commands, which requires ticks
// to keep firing while `isEditor` is true.
watch(isEditor, (nowEditor) => {
  if (!nowEditor && fields.value.startOnLoad !== false && startTimestamp.value === null) {
    startTimer()
  }
})

// If the configured variable key changes at edit-time, push the current
// remaining value to the new key so a downstream interpolation updates
// immediately rather than waiting a full second.
watch(
  () => fields.value.valueVariable?.key,
  () => writeVariable(remaining.value),
)

// If the initial duration changes while paused/reset, reflect the new value
// right away so any interpolation tracks the field edit without waiting for
// a tick.
watch(initialSeconds, (next) => {
  if (startTimestamp.value === null) {
    durationAtStart.value = next
    remaining.value = next
    writeVariable(remaining.value)
  }
})
</script>
